import { Book } from '../Types/Book';
import { CreatorFilter } from '../Types/CreatorFilter';
import { StorylineDilemmaChoiceInput } from '../Types/StorylineDilemma';
import { UserArticle } from '../Types/UserArticle';
import {
  BOOK_IMAGE_RENDITIONS,
  generateAssetRenditionUrl,
  ImageFormat,
} from '../Utils/Assets';
import { getApiInstance } from './Api';
import { mapUserArticle } from './ArticleApi';

export function getBookCoverImage(imagePath: string) {
  if (!imagePath) {
    return undefined;
  }

  const imageUrl = new URL(imagePath, window.env.REACT_APP_MEDIA_BASE_URL);
  return {
    originalUrl: imageUrl.toString(),
    compressedUrl: generateAssetRenditionUrl(
      imageUrl.toString(),
      null,
      ImageFormat.WEBP,
    ),
    thumbnailUrl: generateAssetRenditionUrl(
      imageUrl.toString(),
      BOOK_IMAGE_RENDITIONS.THUMBNAIL,
      ImageFormat.WEBP,
    ),
    coverUrl: generateAssetRenditionUrl(
      imageUrl.toString(),
      BOOK_IMAGE_RENDITIONS.COVER,
      ImageFormat.WEBP,
    ),
  };
}

export class BookApi {
  static async getBooks(
    userId: string,
    limit?: number,
    afterBookId?: string,
    creatorFilter?: CreatorFilter,
    searchTerm?: string,
  ): Promise<Book[]> {
    let url = `/user-articles/${userId}/books`;
    const params = new URLSearchParams();

    if (limit) {
      params.append('limit', limit.toString());
    }

    if (afterBookId) {
      params.append('afterBookId', afterBookId);
    }

    if (creatorFilter) {
      params.append('creatorFilter', creatorFilter);
    }

    if (searchTerm) {
      params.append('search', searchTerm);
    }

    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }

    const response = await getApiInstance().get(url);
    return response.data.map((book: any) => new Book(book));
  }

  async getBook(bookId: string): Promise<Book> {
    try {
      const response = await getApiInstance().get(`/books/${bookId}`);
      return response.data ? new Book(response.data) : undefined;
    } catch (error) {
      console.error('Error fetching book', error);
      throw error;
    }
  }

  async triggerBookGeneration(params: {
    universeId: string;
    storyline?: string;
    dilemmaChoices?: StorylineDilemmaChoiceInput[];
    coAuthorUserId?: string;
  }): Promise<{ requestId: string; newBookId: string }> {
    try {
      const response = await getApiInstance().post(
        `/books/generate/async`,
        params,
      );
      return response.data;
    } catch (error) {
      console.error('Error triggering book generation', error);
      throw error;
    }
  }

  async getUserBookGenerationStatus(
    requestId: string,
    bookId: string,
  ): Promise<{ status: 'completed' | 'failed' | 'in-progress'; book: Book }> {
    try {
      const response = await getApiInstance().get(
        `/books/generate/status?requestId=${requestId}&bookId=${bookId}`,
      );
      return {
        status: response.data.status,
        book: response.data.book ? new Book(response.data.book) : undefined,
      };
    } catch (error) {
      console.error('Error fetching user book generation status', error);
      throw error;
    }
  }

  async triggerAsyncNextChapterGeneration(params: {
    bookId: string;
    dilemmaChoices: StorylineDilemmaChoiceInput[];
  }): Promise<{ requestId: string; newChapterId: string }> {
    try {
      const { bookId, ...restParams } = params;
      const response = await getApiInstance().post(
        `/books/${bookId}/generate/async`,
        restParams,
      );
      return response.data;
    } catch (error) {
      console.error('Error triggering async chapter generation', error);
      throw error;
    }
  }

  async fetchChapterGenerationStatus(
    requestId: string,
    bookId: string,
    chapterId: string,
  ) {
    try {
      const response = await getApiInstance().get(
        `/books/${bookId}/generate/status?requestId=${requestId}&chapterId=${chapterId}`,
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching chapter generation status', error);
      throw error;
    }
  }

  async generateBookCover(
    bookId: string,
    description: string,
  ): Promise<{ imagePath: string }> {
    try {
      const response = await getApiInstance().post(
        `/books/${bookId}/generate/cover`,
        {
          description,
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error generating book cover', error);
      throw error;
    }
  }

  async publishBook(
    bookId: string,
    params: { title: string; coverImagePath: string },
  ): Promise<Book> {
    try {
      const response = await getApiInstance().post(
        `/books/${bookId}/publish`,
        params,
      );
      return response.data ? new Book(response.data) : undefined;
    } catch (error) {
      console.error('Error publishing book:', error);
      throw error;
    }
  }

  async triggerGenerateBookContentVideo(
    bookId: string,
  ): Promise<{ requestId: string }> {
    try {
      const response = await getApiInstance().post(
        `/video/book/${bookId}/content-video`,
      );
      return response.data;
    } catch (error) {
      console.error('Error generating book video:', error);
      throw error;
    }
  }

  async getBookUserArticles(bookId: string): Promise<UserArticle[]> {
    try {
      const response = await getApiInstance().get(
        `/books/${bookId}/user-articles`,
      );
      return response.data.map((userArticle: any) =>
        mapUserArticle(userArticle),
      );
    } catch (error) {
      console.error('Error fetching book user articles', error);
      throw error;
    }
  }

  async editBook(
    bookId: string,
    title: string,
    coverImagePath: string,
  ): Promise<Book> {
    try {
      const response = await getApiInstance().put(`/books/${bookId}`, {
        title,
        coverImagePath,
      });
      return new Book(response.data);
    } catch (error) {
      console.error('Error editing book:', error);
      throw error;
    }
  }

  /**
   * Deletes a book or removes it from the user's list
   * If the user is an author of the book, it will be soft deleted
   * If the user is not an author, it will be removed from their list
   *
   * @param bookId The ID of the book to delete
   * @returns A promise that resolves when the operation is complete
   */
  async abandonBook(bookId: string): Promise<void> {
    try {
      await getApiInstance().delete(`/user-articles/books/${bookId}/abandon`);
    } catch (error) {
      console.error('Error abandoning book:', error);
      throw error;
    }
  }
}
